import { useTranslation } from 'react-i18next';
import { Modal } from '../../UI';
import { Button } from '../../UI/base/Button';
import modalStyles from '../../UI/Modal.module.css';
import styles from './DeleteBookingModal.module.css';

// Narrow confirmation dialog shared by every booking type's delete flow. Only the
// i18n keys and the resolved subject differ per type, so callers pass those in.
const MODAL_WIDTH = 480;

interface DeleteBookingModalProps {
  /** i18n key for the modal title. */
  titleKey: string;
  /** i18n key for the confirm button. */
  confirmKey: string;
  /** i18n key for the single-occurrence message; receives `{ name }`. */
  messageKey: string;
  /**
   * i18n key for the recurring message; receives `{ name, pattern }`. Omit for
   * booking types that have no recurrence (e.g. closed days).
   */
  recurringMessageKey?: string;
  /** What is being deleted: the joined entity names, a mode label, or a period. */
  name: string;
  /** Recurrence summary when the target repeats — switches to the all-occurrences warning. */
  recurrenceSummary?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export const DeleteBookingModal = ({
  titleKey,
  confirmKey,
  messageKey,
  recurringMessageKey,
  name,
  recurrenceSummary,
  onConfirm,
  onClose
}: DeleteBookingModalProps) => {
  const { t } = useTranslation();
  const isRecurring = Boolean(recurrenceSummary && recurringMessageKey);

  return (
    <Modal
      title={t(titleKey)}
      onClose={onClose}
      width={MODAL_WIDTH}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            {t(confirmKey)}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.message}>
          {isRecurring
            ? t(recurringMessageKey as string, { name, pattern: recurrenceSummary })
            : t(messageKey, { name })}
        </p>
      </div>
    </Modal>
  );
};
